import { useEffect, useMemo, useState } from 'react';
import {
  Deployments,
  Tickets,
  type DeploymentDisposition,
  type DeploymentItemInput,
  type DeploymentKind,
  type PostDeployOriginalStatus,
  type TicketSummary,
} from '../lib/api';
import { Dialog } from './ui/Dialog';

interface Props {
  onClose: () => void;
  onSaved: () => void;
}

const DISPOSITIONS: { value: DeploymentDisposition; label: string }[] = [
  { value: 'PENDING_REPORTER_REVIEW', label: 'Pending reporter review' },
  { value: 'PENDING_CUSTOMER_REVIEW', label: 'Pending customer review' },
  { value: 'RESOLVED', label: 'Resolved (already verified)' },
];

// When a deploy ticket is resolved here, what its linked ORIGINAL ticket should
// do. Default 'leave' keeps the pre-#338 behaviour: only the deploy ticket moves.
const ORIGINAL_DISPOSITIONS: { value: PostDeployOriginalStatus; label: string }[] = [
  { value: 'leave', label: 'Leave as-is' },
  { value: 'PENDING_REPORTER_REVIEW', label: 'Pending reporter review' },
  { value: 'PENDING_CUSTOMER_REVIEW', label: 'Pending customer review' },
  { value: 'RESOLVED', label: 'Resolved' },
];

// Deploy tickets are closed by the act of deploying, so they default to
// Resolved; everything else defaults to reporter review.
function defaultDisposition(t: TicketSummary): DeploymentDisposition {
  return t.isDeployTicket ? 'RESOLVED' : 'PENDING_REPORTER_REVIEW';
}

// One line in the picker: a ticket plus, when its deploy ticket is also in
// Ready to Deploy, that deploy ticket — shipped together as one change.
interface Row {
  ticket: TicketSummary;
  deploy: TicketSummary | null;
}

// BUGFIX batches Ready-to-Deploy tickets (the original flow). Every other kind
// is a standalone push with no issue ticket — it carries its own title instead.
const KINDS: { value: DeploymentKind; label: string }[] = [
  { value: 'BUGFIX', label: 'Bug fixes (from Ready to Deploy)' },
  { value: 'FEATURE', label: 'Feature' },
  { value: 'INFRA', label: 'Infrastructure' },
  { value: 'CONFIG', label: 'Config change' },
  { value: 'DEPENDENCY', label: 'Dependency update' },
  { value: 'HOTFIX', label: 'Hotfix' },
  { value: 'ROLLBACK', label: 'Rollback' },
  { value: 'OTHER', label: 'Other' },
];

/**
 * Admin-only "Record deployment" dialog. A deployment is one of two shapes:
 *
 *   - Bug fixes: pick tickets currently in Ready to Deploy and choose each
 *     one's post-deploy status. Status flips + emails happen server-side.
 *     An original and its deploy ticket are shown as one row: picking it
 *     resolves the deploy ticket and moves the original to the chosen status.
 *   - Anything else (feature, infra, config, dependency, hotfix, rollback):
 *     a standalone record with a title and optional reference URL — no issue
 *     ticket required. This is the case the old flow couldn't represent.
 *
 * A free-text Notes field captures release info (build SHA, services touched)
 * in either shape.
 */
export function DeploymentDialog({ onClose, onSaved }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tickets, setTickets] = useState<TicketSummary[]>([]);
  const [selected, setSelected] = useState<Map<number, DeploymentDisposition>>(
    new Map(),
  );
  // Per deploy-ticket: where its linked original goes when it's resolved here.
  // Only read for deploy-ticket rows set to RESOLVED; defaults to 'leave'.
  const [originalDisposition, setOriginalDisposition] = useState<
    Map<number, PostDeployOriginalStatus>
  >(new Map());
  const [kind, setKind] = useState<DeploymentKind>('BUGFIX');
  const [title, setTitle] = useState('');
  const [refUrl, setRefUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isBugfix = kind === 'BUGFIX';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Pull the queue. Limit high enough to comfortably show all currently
        // shippable tickets without paginating.
        const res = await Tickets.list({ status: 'READY_TO_DEPLOY', limit: 100, view: 'all' });
        if (cancelled) return;
        setTickets(res.tickets);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load tickets.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const ticketsById = useMemo(
    () => new Map(tickets.map((t) => [t.id, t] as const)),
    [tickets],
  );

  // Fold each deploy ticket into its original's row when both are in Ready to
  // Deploy. A deploy ticket whose original isn't in the queue stays its own row.
  const rows: Row[] = useMemo(() => {
    const deployByOriginal = new Map<number, TicketSummary>();
    for (const t of tickets) {
      if (t.isDeployTicket && t.deployOfTicketId && ticketsById.has(t.deployOfTicketId)) {
        deployByOriginal.set(t.deployOfTicketId, t);
      }
    }
    const folded = new Set(Array.from(deployByOriginal.values(), (d) => d.id));
    return tickets
      .filter((t) => !folded.has(t.id))
      .map((t) => ({ ticket: t, deploy: deployByOriginal.get(t.id) ?? null }));
  }, [tickets, ticketsById]);

  const rowsById = useMemo(
    () => new Map(rows.map((r) => [r.ticket.id, r] as const)),
    [rows],
  );

  const itemsToSubmit: DeploymentItemInput[] = useMemo(
    () =>
      Array.from(selected.entries()).flatMap(([ticketId, status]) => {
        const item: DeploymentItemInput = { ticketId, status };
        // Carry the original's disposition only for a resolved deploy ticket
        // with a non-default choice — the backend ignores it otherwise.
        if (status === 'RESOLVED' && ticketsById.get(ticketId)?.isDeployTicket) {
          const choice = originalDisposition.get(ticketId) ?? 'leave';
          if (choice !== 'leave') item.postDeployOriginalStatus = choice;
        }
        // A folded deploy ticket ships with its original and is resolved. The
        // original is moved directly by its own item, so no cascade is needed.
        const deploy = rowsById.get(ticketId)?.deploy;
        return deploy ? [item, { ticketId: deploy.id, status: 'RESOLVED' as const }] : [item];
      }),
    [selected, originalDisposition, ticketsById, rowsById],
  );

  // Bug-fix deploys need ≥1 ticket; standalone deploys need a title.
  const canSubmit = isBugfix ? itemsToSubmit.length > 0 : !!title.trim();

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else {
        const t = ticketsById.get(id);
        if (t) next.set(id, defaultDisposition(t));
      }
      return next;
    });
  }

  function setDispositionFor(id: number, status: DeploymentDisposition) {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.set(id, status);
      return next;
    });
  }

  function setOriginalDispositionFor(id: number, status: PostDeployOriginalStatus) {
    setOriginalDisposition((prev) => {
      const next = new Map(prev);
      next.set(id, status);
      return next;
    });
  }

  function selectAll() {
    const next = new Map(selected);
    for (const { ticket: t } of rows) if (!next.has(t.id)) next.set(t.id, defaultDisposition(t));
    setSelected(next);
  }

  function clearAll() {
    setSelected(new Map());
  }

  async function submit() {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await Deployments.create({
        kind,
        title: title.trim() || undefined,
        refUrl: refUrl.trim() || undefined,
        notes: notes.trim() || undefined,
        items: isBugfix ? itemsToSubmit : [],
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to record deployment.');
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => { if (!o && !submitting) onClose(); }}
      title="Record deployment"
      description="Log what shipped to production. Bug-fix deploys move Ready-to-Deploy tickets; everything else is recorded on its own."
      size="max-w-2xl"
      footer={
        <>
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 rounded-md border border-slate-300 text-sm hover:bg-slate-50 disabled:opacity-60"
            type="button"
          >
            Cancel
          </button>
          <button
            onClick={() => void submit()}
            disabled={submitting || !canSubmit}
            className="px-3 py-1.5 rounded-md bg-brand-600 text-white text-sm hover:bg-brand-700 disabled:opacity-60"
            type="button"
          >
            {submitting
              ? 'Recording…'
              : isBugfix
                ? `Record deployment (${itemsToSubmit.length})`
                : 'Record deployment'}
          </button>
        </>
      }
    >
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Deployment type
            </label>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as DeploymentKind)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
            >
              {KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
          </div>

          {isBugfix ? (
            loading ? (
              <div className="text-sm text-slate-500">Loading Ready-to-Deploy tickets…</div>
            ) : tickets.length === 0 ? (
              <div className="text-sm text-slate-500 italic">
                No tickets are currently in Ready to Deploy.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">
                    {rows.length} change{rows.length === 1 ? '' : 's'} in Ready to Deploy
                    {rows.length !== tickets.length && ` (${tickets.length} tickets incl. deploy tickets)`}
                    {selected.size > 0 && ` · ${selected.size} selected`}
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={selectAll}
                      className="text-brand-700 hover:text-brand-800 underline"
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={clearAll}
                      className="text-slate-500 hover:text-slate-700 underline"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
                  {rows.map(({ ticket: t, deploy }) => {
                    const isSelected = selected.has(t.id);
                    return (
                      <div
                        key={t.id}
                        className="px-3 py-2 hover:bg-slate-50"
                      >
                        <div className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggle(t.id)}
                            className="mt-1"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-slate-900 truncate">
                              <span className="text-slate-500">#{t.id}</span> {t.title}
                            </div>
                            {t.assignee && (
                              <div className="text-xs text-slate-500">
                                assignee: {t.assignee.name}
                              </div>
                            )}
                            {deploy && (
                              <div className="text-xs text-slate-500">
                                + deploy ticket #{deploy.id}
                                {isSelected && ' → Resolved'}
                              </div>
                            )}
                          </div>
                          <select
                            disabled={!isSelected}
                            value={selected.get(t.id) ?? defaultDisposition(t)}
                            onChange={(e) =>
                              setDispositionFor(t.id, e.target.value as DeploymentDisposition)
                            }
                            className="text-xs px-2 py-1 border border-slate-300 rounded-md bg-white disabled:bg-slate-100 disabled:text-slate-400"
                          >
                            {DISPOSITIONS.map((d) => (
                              <option key={d.value} value={d.value}>
                                {d.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {/* For a deploy ticket being resolved, let the admin advance its
                            linked original in the same step (ticket #338). */}
                        {isSelected &&
                          t.isDeployTicket &&
                          selected.get(t.id) === 'RESOLVED' && (
                            <div className="mt-2 ml-6 flex items-center gap-2 text-xs text-slate-500">
                              <span>Original ticket →</span>
                              <select
                                value={originalDisposition.get(t.id) ?? 'leave'}
                                onChange={(e) =>
                                  setOriginalDispositionFor(
                                    t.id,
                                    e.target.value as PostDeployOriginalStatus,
                                  )
                                }
                                className="text-xs px-2 py-1 border border-slate-300 rounded-md bg-white"
                              >
                                {ORIGINAL_DISPOSITIONS.map((d) => (
                                  <option key={d.value} value={d.value}>
                                    {d.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>
              </>
            )
          ) : (
            <>
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Title <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Bump web deps + Stripe SDK v14"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Reference URL (optional)
                </label>
                <input
                  type="url"
                  value={refUrl}
                  onChange={(e) => setRefUrl(e.target.value)}
                  placeholder="PR, commit, release, or runbook link"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Deployment notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="What was deployed? Services touched, build SHA, gotchas…"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
    </Dialog>
  );
}
